import { ChevronRight, Plus, Scale } from 'lucide-react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AppHeaderContent } from '@/components/AppHeaderContent';
import { useEntityId } from '@/lib/offline/operations';
import { useCamadaActiva, useCamadaDetalle } from '@/lib/queries/camadas';
import { useLotesDeCamada } from '@/lib/queries/lotesNave';
import { usePesajesDeLote } from '@/lib/queries/pesajes';
import { fmtFecha, fmtInt } from '@/lib/format';
import { formatoDecimal } from '@/lib/liquidacion';
import { useAuthStore } from '@/stores/auth';

/** Alias de nave: resuelve su lote activo sin una transición intermedia. */
export function NaveMuestreosAliasPage() {
  const { naveId: parameter } = useParams();
  const naveId = useEntityId(parameter, 'naves');
  const [search] = useSearchParams();
  const camadaId = useEntityId(search.get('camada'), 'camadas');
  return search.has('camada') ? (
    <MuestreosListado camadaId={camadaId} loteId={null} naveId={naveId} desdeNave />
  ) : (
    <ActiveNaveMuestreosAliasPage />
  );
}

function ActiveNaveMuestreosAliasPage() {
  const { t } = useTranslation();
  const { naveId: naveIdParam } = useParams<{ naveId: string }>();
  const naveId = useEntityId(naveIdParam, 'naves');
  const camadaActivaQ = useCamadaActiva();
  const camadaId = camadaActivaQ.data?.id ?? null;
  const lotesQ = useLotesDeCamada(camadaId);
  const lote = (lotesQ.data ?? []).find((item) => item.nave_id === naveId) ?? null;
  const loading = camadaActivaQ.isLoading || lotesQ.isLoading;
  const error = camadaActivaQ.isError || lotesQ.isError || naveId == null;

  if (error || loading) {
    return (
      <main className="cm-form-page mx-auto max-w-md px-4">
        <header className="ui-global-header" data-testid="app-header">
          <AppHeaderContent backTo={naveId == null ? '/' : `/naves/${naveId}`} backLabel="Nave" />
        </header>
        <h1 className="ui-page-title">Muestreos</h1>
        {error ? (
          <p role="alert" className="text-warning-text">
            {t('common.error')}
          </p>
        ) : (
          <p role="status" className="text-text-secondary">
            {t('common.loading')}
          </p>
        )}
      </main>
    );
  }
  if (!camadaId || !lote) {
    return <Navigate to={`/naves/${naveId}`} replace />;
  }
  return <MuestreosListado camadaId={camadaId} loteId={lote.id} desdeNave />;
}

/** Historial del lote exacto, incluida consulta de los gramos originales. */
function MuestreosListado({
  camadaId,
  loteId,
  desdeNave = false,
  naveId,
}: {
  camadaId: number | null;
  loteId: number | null;
  desdeNave?: boolean;
  naveId?: number | null;
}) {
  const { t } = useTranslation();
  const detalleQ = useCamadaDetalle(camadaId);
  const camada = detalleQ.data?.camada ?? null;
  const lote =
    detalleQ.data?.lotes.find((item) =>
      naveId != null ? item.nave_id === naveId : item.lote_id === loteId,
    ) ?? null;
  const pesajesQ = usePesajesDeLote(lote?.lote_id ?? null);
  const user = useAuthStore((state) => state.user);
  const loading = detalleQ.isLoading || (lote != null && pesajesQ.isLoading);
  const error =
    detalleQ.isError ||
    pesajesQ.isError ||
    (!loading && (!camada || !lote || pesajesQ.data == null));
  const canEdit = (user?.rol === 'admin' || user?.rol === 'ganadero') && camada && !camada.cerrada;
  const pesajes = [...(pesajesQ.data ?? [])].sort(
    (a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id,
  );
  const formPath = lote ? `/naves/${lote.nave_id}/pesajes/nuevo` : '';
  const formContext = `camada=${camadaId}&lote=${lote?.lote_id ?? loteId}&from=listado${desdeNave ? '&back=nave' : ''}`;

  return (
    <main className="cm-form-page mx-auto max-w-md px-4">
      <header className="ui-global-header" data-testid="app-header">
        <AppHeaderContent
          backTo={
            desdeNave && lote
              ? `/naves/${lote.nave_id}?${naveId != null ? `camada=${camadaId}&` : ''}tab=pesos`
              : camadaId == null
                ? '/camadas'
                : `/camadas?camada=${camadaId}`
          }
          backLabel={desdeNave ? 'Nave' : 'Camadas'}
        />
      </header>
      <h1 className="ui-page-title">
        {lote
          ? t('camada_detalle.muestreos_listado_titulo', { nave: lote.nave_codigo })
          : 'Muestreos'}
      </h1>
      {camada && (
        <p className="cm-form-context">
          {camada.codigo_lote}
          <span>
            {!loading && !error && `${pesajes.length} muestreos registrados`}
            {camada.cerrada ? ' · Finalizada' : ''}
          </span>
        </p>
      )}
      {error ? (
        <p role="alert" className="text-warning-text">
          {t('common.error')}
        </p>
      ) : loading ? (
        <p role="status" className="text-text-secondary">
          {t('common.loading')}
        </p>
      ) : (
        <>
          {canEdit ? (
            <Link to={`${formPath}?${formContext}`} className="ui-primary">
              <Scale size={20} aria-hidden />
              {t('camada_detalle.muestreos_registrar')}
              <Plus size={18} aria-hidden />
            </Link>
          ) : (
            <p className="cm-form-note">
              {camada!.cerrada
                ? 'La camada está cerrada. Puedes consultar los muestreos.'
                : 'Tu perfil permite consultar los muestreos.'}
            </p>
          )}
          {pesajes.length === 0 ? (
            <p className="cm-form-note">{t('camada_detalle.muestreos_vacio')}</p>
          ) : (
            <ul className="cm-arrival-list" data-testid="camada-listado-muestreos">
              {pesajes.map((pesaje) => (
                <li key={pesaje.id}>
                  <details className="group">
                    <summary
                      className="ui-menu-row cm-arrival-row cursor-pointer list-none"
                      data-testid={`camada-muestreo-${pesaje.id}`}
                      aria-label={`Consultar muestreo: ${fmtFecha(pesaje.fecha)}`}
                    >
                      <Scale size={22} aria-hidden />
                      <span>
                        <span className="flex items-baseline justify-between gap-3">
                          <strong>{fmtFecha(pesaje.fecha)}</strong>
                          <strong className="shrink-0 tabular-nums">
                            {formatoDecimal(pesaje.peso_medio_kg, 3)} kg
                          </strong>
                        </span>
                        <small>{t('camada_detalle.muestreo_pavos', { n: pesaje.num_pavos })}</small>
                      </span>
                      <ChevronRight size={18} className="group-open:rotate-90" aria-hidden />
                    </summary>
                    <div className="space-y-3 border-b border-border py-3">
                      <h2 className="text-sm font-medium">Pesos individuales (g)</h2>
                      <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {pesaje.pesos_g.map((gramos, i) => (
                          <div key={i} className="flex justify-between gap-2">
                            <dt className="text-text-secondary">
                              {t('pesajes.pavo')} {i + 1}
                            </dt>
                            <dd className="tabular-nums">{fmtInt(gramos)} g</dd>
                          </div>
                        ))}
                      </dl>
                      {canEdit && (
                        <Link
                          to={`${formPath}?pesaje=${pesaje.id}&${formContext}`}
                          className="ui-secondary w-full"
                          aria-label={`${t('pesajes.registrar.tituloEditar')}: ${fmtFecha(pesaje.fecha)}`}
                        >
                          {t('pesajes.registrar.tituloEditar')}
                        </Link>
                      )}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}

export function CamadaMuestreosPage() {
  const { camadaId: camadaIdParam, loteId: loteIdParam } = useParams<{
    camadaId: string;
    loteId: string;
  }>();
  const camadaId = useEntityId(camadaIdParam, 'camadas');
  const loteId = useEntityId(loteIdParam, 'lotes');
  return <MuestreosListado camadaId={camadaId} loteId={loteId} />;
}
